/**
 * SessionDetailPanel — panel derecho con detalle de la sesión seleccionada
 *
 * @remarks
 * Muestra FileUploadPanel cuando la sesión está en draft/uploading/failed,
 * y ProcessingProgress para todos los estados.
 * Usa useSurveySession para poder refrescar el estado individualmente.
 *
 * @module survey/components
 */

import { useSurveySession } from '../hooks/useSurveySession';
import type { SurveySession } from '../types/survey.types';
import { FileUploadPanel } from './FileUploadPanel';
import { ProcessingProgress } from './ProcessingProgress';
import { SessionStatusBadge } from './SessionStatusBadge';

interface Props {
  initialSession: SurveySession;
  onClose: () => void;
  onOpenViewer?: (session: SurveySession) => void;
}

const UPLOAD_STATUSES = new Set(['draft', 'uploading', 'failed']);

function SessionHeader({
  session,
  onClose,
}: {
  session: SurveySession;
  onClose: () => void;
}): JSX.Element {
  return (
    <div className="sw-detail-header">
      <div className="sw-detail-title-row">
        <h3 className="sw-detail-title">{session.name}</h3>
        <button type="button" className="sw-detail-close" onClick={onClose}>
          ✕
        </button>
      </div>
      <div className="sw-detail-meta">
        <SessionStatusBadge status={session.status} />
        {session.captureDate && <span className="sw-detail-date">{session.captureDate}</span>}
      </div>
    </div>
  );
}

export function SessionDetailPanel({ initialSession, onClose, onOpenViewer }: Props): JSX.Element {
  const { session, reload } = useSurveySession({ sessionId: initialSession.id });
  const current = session ?? initialSession;
  const showUpload = UPLOAD_STATUSES.has(current.status);

  return (
    <div className="sw-detail-panel">
      <SessionHeader session={current} onClose={onClose} />

      <div className="sw-detail-body">
        {showUpload && <FileUploadPanel sessionId={current.id} onUploaded={() => void reload()} />}

        {current.status !== 'draft' && (
          <ProcessingProgress
            session={current}
            onRefresh={reload}
            onTriggered={() => void reload()}
          />
        )}

        {current.status === 'draft' && (
          <p className="sw-detail-hint">Subí un GPX y un video para habilitar el procesamiento.</p>
        )}

        {current.status === 'processed' && onOpenViewer && (
          <button
            type="button"
            className="sw-btn sw-btn-primary"
            onClick={() => onOpenViewer(current)}
          >
            Ver capturas
          </button>
        )}
      </div>
    </div>
  );
}
